<template>
  <div class="text-editor" :class="{'is-invalid': isInvalid}">
    <Editor
      ref="tinymce"
      :init="tinyInit"
      @change="changeContent"
      :initial-value="modelValue"
    />
  </div>

  <Dialog
    v-model:visible="pricingDialogOpen"
    modal
    header="Preistabelle konfigurieren"
    :style="{ width: '90vw', maxWidth: '980px' }"
  >
    <div class="row g-3">
      <div class="col-12 col-lg-4">
        <label class="form-label">Tabellen-ID</label>
        <input v-model="pricingState.id" type="text" class="form-control">
      </div>
      <div class="col-12 col-lg-4">
        <label class="form-label">Titel</label>
        <input v-model="pricingState.caption" type="text" class="form-control">
      </div>
      <div class="col-12 col-lg-4">
        <label class="form-label">Untertitel</label>
        <input v-model="pricingState.subCaption" type="text" class="form-control">
      </div>
    </div>

    <div class="row g-3 mt-1">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h4 class="card-title h5 mb-0">Spalten (z.B. Saisonszeiten)</h4>
              <button class="btn btn-outline-primary btn-sm" type="button" @click="addColumn">Spalte hinzufügen</button>
            </div>
            <div
              v-for="(column, index) in pricingState.columns"
              :key="`column-${index}`"
              class="border-top pt-3 mt-3"
              :class="{'border-top-0 pt-0 mt-0': index === 0}"
            >
              <div class="row g-3 align-items-end">
                <div class="col-12 col-lg-4">
                  <label class="form-label">Name</label>
                  <input v-model="column.name" type="text" class="form-control">
                </div>
                <div class="col-12 col-lg-5">
                  <label class="form-label">Info (z.B. Saison-Zeiträume)</label>
                  <textarea v-model="column.info" rows="3" class="form-control"></textarea>
                </div>
                <div class="col-12 col-lg-3">
                  <div class="d-flex flex-wrap gap-2">
                    <button class="btn btn-outline-secondary btn-sm" type="button" @click="moveColumn(index, -1)">↑</button>
                    <button class="btn btn-outline-secondary btn-sm" type="button" @click="moveColumn(index, 1)">↓</button>
                    <button
                      class="btn btn-outline-danger btn-sm"
                      type="button"
                      @click="deleteColumn(index)"
                      :disabled="pricingState.columns.length <= 1"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <h4 class="card-title h5 mb-0">Zeilen</h4>
              <button class="btn btn-outline-primary btn-sm" type="button" @click="addRow">Zeile hinzufügen</button>
            </div>
            <div
              v-for="(row, rowIndex) in pricingState.rows"
              :key="`row-${rowIndex}`"
              class="border-top pt-3 mt-3"
              :class="{'border-top-0 pt-0 mt-0': rowIndex === 0}"
            >
              <div class="row g-3 align-items-end">
                <div class="col-12 col-xl-3">
                  <label class="form-label">Überschrift (th)</label>
                  <input v-model="row.header" type="text" class="form-control">
                </div>
                <div
                  v-for="(column, colIndex) in pricingState.columns"
                  :key="`row-${rowIndex}-col-${colIndex}`"
                  class="col-12 col-md-6 col-xl"
                >
                  <label class="form-label">{{ columnCellLabel(column, colIndex) }}</label>
                  <input v-model="row.cells[colIndex]" type="text" class="form-control">
                </div>
                <div class="col-12 col-xl-auto">
                  <div class="d-flex flex-wrap gap-2">
                    <button class="btn btn-outline-secondary btn-sm" type="button" @click="moveRow(rowIndex, -1)">↑</button>
                    <button class="btn btn-outline-secondary btn-sm" type="button" @click="moveRow(rowIndex, 1)">↓</button>
                    <button
                      class="btn btn-outline-danger btn-sm"
                      type="button"
                      @click="deleteRow(rowIndex)"
                      :disabled="pricingState.rows.length <= 1"
                    >
                      Löschen
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <template #footer>
      <button type="button" class="btn btn-outline-secondary" @click="cancelPricingDialog">Abbrechen</button>
      <button type="button" class="btn btn-primary" @click="savePricingDialog">Speichern</button>
    </template>
  </Dialog>
</template>

<script>
import 'hugerte/hugerte'
import 'hugerte/icons/default/icons'
import 'hugerte/themes/silver/theme'
import 'hugerte/models/dom/model'
import 'hugerte/skins/ui/oxide/skin.css'

import 'hugerte/models/dom';
import 'hugerte/plugins/lists';
import 'hugerte/plugins/searchreplace';
import 'hugerte/plugins/table';
import 'hugerte/plugins/code';
import 'hugerte/plugins/quickbars';

import Editor from '@hugerte/hugerte-vue'
import Dialog from 'primevue/dialog';

import {ref} from 'vue';
import {useUserStore} from '@/stores/user';

import {
  syncRowCellCount,
  columnCellLabel,
  createDefaultState,
  buildPricingHtml,
  moveItem,
  registerBasis3PricingTablePlugin,
  stripFormattingFromHtml,
  stripFormattingFromNode,
  EDITOR_CONTENT_SCHEMA
} from '@/scripts/hugerte';

const EDITOR_CONTENT_STYLE = `
  .mce-offscreen-selection {
    position: fixed !important;
    left: -10000px !important;
    top: -10000px !important;
    width: 1px !important;
    height: 1px !important;
    overflow: hidden !important;
    opacity: 0 !important;
    pointer-events: none !important;
    display: block !important;
  }
  figure.pricingtable.pricingtable-selected {
    outline: 2px solid #2271b1 !important;
    outline-offset: 2px;
    background: rgba(34, 113, 177, 0.06) !important;
  }
  p[data-mce-caret][data-mce-bogus] {
    position: absolute !important;
    inset: auto !important;
    width: 0 !important;
    height: 0 !important;
    overflow: hidden !important;
    margin: 0 !important;
    padding: 0 !important;
    line-height: 0 !important;
    opacity: 0 !important;
    pointer-events: none !important;
    border: 0 !important;
  }
  div.mce-visual-caret {
    opacity: 0 !important;
    pointer-events: none !important;
  }
  .pricingtable caption {
    caption-side: top;
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
    color: rgba(33, 37, 41, 0.75);
    text-align: left;
  }
  .pricingtable .caption-lead
  {
    font-size: $font-size-base * 1.15;
    margin-top: 1rem;
    margin-bottom: 1.5rem;
    color: black;
    font-weight: 300;
  }

  .pricingtable .caption-title
  {
    margin:0;
    color: black;
    font-size: 1.5rem;
    font-weight: bold;
  }

  .pricingtable table {
    border: none;
    width: 100%;
    border-collapse: collapse;
    text-align: left;
  }
  .pricingtable table .responsive_caption {
    width: auto;
    padding: 0.4em 0;
    text-align: left;
    font-style: italic;
  }
  .pricingtable table thead th {
    vertical-align: bottom;
    background: #f6f6f6;
    border: none;
    padding: 0.5em;
  }
  .pricingtable table thead th:first-child {
    padding: 2em;
  }
  .pricingtable table thead th:not(:first-child) {
    border-bottom: 1px solid lightgray;
    text-transform: uppercase;
    font-weight: 400;
  }
  .pricingtable table thead td {
    background: #f6f6f6;
  }
  .pricingtable table tbody th {
    border-block: 1px solid lightgray;
    border-inline: none;
    font-weight: 300;
    background-color: white;
    padding: 0.75rem;
  }
  .pricingtable table tbody td {
    border: none;
    border-block: 1px solid lightgray;
    vertical-align: top;
  }
  .pricingtable table td {
    border: none;
    border-bottom: 1px solid lightgray;
    vertical-align: top;
    padding: 0.5em;
  }
  .pricingtable table .responsive_caption {
    padding: 0.75em;
  }
`;

const EDITOR_BLOCK_FORMATS = 'Absatz=p; Überschrift 2=h2; Überschrift 3=h3; Überschrift 4=h4';

const EDITOR_MENUBAR = 'edit insert format';

const EDITOR_MENU = {
  edit: {title: 'Edit', items: 'undo redo | cut copy paste | selectall | searchreplace'},
  insert: {title: 'Insert', items: 'hr'},
  format: {title: 'Format', items: 'blocks'}
};

export default {
  components: {
    Dialog,
    Editor
  },
  setup(props) {
    const userStore = useUserStore();
    const pricingDialogOpen = ref(false);
    const pricingState = ref(createDefaultState());
    const pricingEditor = ref(null);
    const pricingTargetFigure = ref(null);
    const codeButtonEnabled = props.codeEnabled || !!userStore.user?.isStaffMember?.();

    if (props.pricingTablesEnabled) {
      registerBasis3PricingTablePlugin();
      window.__basis3PricingTableOpenCallback = function (editor, state, figure) {
        pricingEditor.value = editor;
        pricingTargetFigure.value = figure;
        pricingState.value = JSON.parse(JSON.stringify(state));
        syncRowCellCount(pricingState.value);
        pricingDialogOpen.value = true;
      };
    }

    const basePlugins = ['lists', 'searchreplace', 'quickbars'];
    if (props.tablesEnabled) {
      basePlugins.push('table');
    }
    if (codeButtonEnabled) {
      basePlugins.push('code');
    }
    if (props.pricingTablesEnabled) {
      basePlugins.push('basis3PricingTable');
    }
    const editorPlugins = basePlugins;

    const toolbarItems = ['undo redo', 'bold italic'];
    if (props.tablesEnabled) {
      toolbarItems.push('table');
    }
    if (codeButtonEnabled) {
      toolbarItems.push('code');
    }
    if (props.pricingTablesEnabled) {
      toolbarItems.push('basis3PricingTable');
    }
    const editorToolbar = toolbarItems.join(' | ');

    const tinyInit = {
      skin: false,
      menubar: EDITOR_MENUBAR,
      menu: EDITOR_MENU,
      block_formats: EDITOR_BLOCK_FORMATS,
      quickbars_selection_toolbar: 'bold italic',
      quickbars_insert_toolbar: false,
      plugins: editorPlugins.join(' '),
      toolbar: editorToolbar,
      branding: false,
      content_css: false,
      content_style: EDITOR_CONTENT_STYLE,
      table_resize_bars: false,
      language_url: '/assets/hugerte/de.js',
      language: 'de',
      ...(props.height && { height: props.height }),
      // Shared with Admin2; see the comment at EDITOR_CONTENT_SCHEMA.
      ...EDITOR_CONTENT_SCHEMA,
      smart_paste: false,
      paste_as_text: true,
      paste_merge_formats: false,
      paste_preprocess(_editor, args) {
        args.content = stripFormattingFromHtml(args.content);
      },
      paste_postprocess(_editor, args) {
        stripFormattingFromNode(args.node);
      }
    };

    return {
      columnCellLabel,
      tinyInit,
      pricingDialogOpen,
      pricingState,
      pricingEditor,
      pricingTargetFigure
    };
  },
  props: {
    modelValue: {
      type: String,
      default: ''
    },
    maxLimit: {
      type: Number,
      default: null
    },
    isInvalid: {
      type: Boolean,
      default: false
    },
    tablesEnabled: {
      type: Boolean,
      default: false
    },
    codeEnabled: {
      type: Boolean,
      default: false
    },
    pricingTablesEnabled: {
      type: Boolean,
      default: false
    },
    height: {
      type: Number,
      default: null
    }
  },
  emits: ['editorChange'],
  methods: {
    changeContent(e, editor) {
      this.$emit('editorChange', editor.getContent());
    },
    addColumn() {
      this.pricingState.columns.push({
        name: `Spalte ${this.pricingState.columns.length + 1}`,
        info: ''
      });
      syncRowCellCount(this.pricingState);
    },
    deleteColumn(index) {
      if (this.pricingState.columns.length <= 1) {
        return;
      }
      this.pricingState.columns.splice(index, 1);
      syncRowCellCount(this.pricingState);
    },
    moveColumn(index, direction) {
      const nextIndex = index + direction;
      moveItem(this.pricingState.columns, index, nextIndex);
      this.pricingState.rows.forEach(function (row) {
        moveItem(row.cells, index, nextIndex);
      });
      syncRowCellCount(this.pricingState);
    },
    addRow() {
      this.pricingState.rows.push({
        header: '',
        cells: new Array(this.pricingState.columns.length).fill('-')
      });
    },
    deleteRow(index) {
      if (this.pricingState.rows.length <= 1) {
        return;
      }
      this.pricingState.rows.splice(index, 1);
    },
    moveRow(index, direction) {
      moveItem(this.pricingState.rows, index, index + direction);
    },
    cancelPricingDialog() {
      this.pricingDialogOpen = false;
      this.pricingEditor = null;
      this.pricingTargetFigure = null;
    },
    savePricingDialog() {
      try {
        syncRowCellCount(this.pricingState);
        const html = buildPricingHtml(this.pricingState);

        if (!this.pricingEditor) {
          this.cancelPricingDialog();
          return;
        }

        if (this.pricingTargetFigure) {
          this.pricingEditor.dom.setOuterHTML(this.pricingTargetFigure, html);
        } else {
          this.pricingEditor.insertContent(html);
        }

        this.$emit('editorChange', this.pricingEditor.getContent({format: 'raw'}));

        this.cancelPricingDialog();
      } catch (error) {
        window.alert(error.message || 'Ungültige Eingaben');
      }
    }
  },
  beforeUnmount() {
    if (window.__basis3PricingTableOpenCallback) {
      delete window.__basis3PricingTableOpenCallback;
    }
  }
}
</script>

<style lang="scss">
.mce-offscreen-selection {
  position: fixed !important;
  left: -10000px !important;
  top: -10000px !important;
  width: 1px !important;
  height: 1px !important;
  overflow: hidden !important;
  opacity: 0 !important;
  pointer-events: none !important;
}



p[data-mce-caret][data-mce-bogus] {
  position: absolute !important;
  inset: auto !important;
  width: 0 !important;
  height: 0 !important;
  overflow: hidden !important;
  margin: 0 !important;
  padding: 0 !important;
  line-height: 0 !important;
  opacity: 0 !important;
  pointer-events: none !important;
  border: 0 !important;
}

div.mce-visual-caret {
  opacity: 0 !important;
  pointer-events: none !important;
}
</style>
